import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../api/axios";

export default function DeleteAccount() {
  const [supportEmail, setSupportEmail] = useState("info@bilimbe.in");

  useEffect(() => {
    const fetchSupportEmail = async () => {
      try {
        const res = await API.get("/settings");
        if (res.data && res.data.supportEmail) {
          setSupportEmail(res.data.supportEmail);
        }
      } catch (err) {
        console.error("Failed to fetch support email:", err);
        // Fallback already set in state
      }
    };
    fetchSupportEmail();
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#050508] flex items-center justify-center p-4 sm:p-8 font-sans relative overflow-hidden text-slate-100">
      
      {/* Dynamic Background Ambient Light */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-red-500/10 rounded-full blur-[160px] animate-pulse pointer-events-none" style={{ animationDuration: '10s' }}></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-amber-500/10 rounded-full blur-[140px] pointer-events-none"></div>

      {/* Grid Overlay Texture */}
      <div className="absolute inset-0 pointer-events-none z-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

      {/* UNIFIED GLASS PLATE CONTAINER */}
      <div className="w-full max-w-[900px] flex flex-col md:flex-row relative z-10 mx-auto min-h-[500px] bg-white/[0.02] border border-white/[0.05] rounded-[3rem] backdrop-blur-[40px] shadow-[0_20px_80px_rgba(0,0,0,0.8)] overflow-hidden">
        
        {/* Subtle top-light gradient */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[80%] h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent z-20"></div>
        
        {/* LEFT COMPONENT - Visual Info */}
        <div className="hidden md:flex flex-1 relative flex-col justify-center items-start p-12 group border-r border-white/5">
          <div className="mb-8">
             <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mb-6 border border-red-500/30">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
             </div>
             <h2 className="text-3xl font-black leading-[1.1] tracking-tighter text-white mb-4">
                Data Privacy &<br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-amber-500">Account Deletion</span>
             </h2>
             <p className="text-sm text-slate-400 font-medium max-w-xs">
                We value your privacy. Requesting account deletion will remove your personal identifiers and associated data from our active systems.
             </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
              <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Secure Verification</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
              <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">7-Day Processing</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
              <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Permanent Removal</span>
            </div>
          </div>

          <div className="absolute bottom-10 left-10 flex gap-3 text-[10px] font-black tracking-[0.2em] text-slate-600 uppercase items-center">
             <div className="h-[1px] w-8 bg-slate-800"></div>
             Bilimbe Digital Compliance
          </div>
        </div>

        {/* RIGHT COMPONENT - Request Details */}
        <div className="flex-[1.2] w-full flex flex-col justify-center px-8 py-12 lg:px-12 relative z-10">
          
          <div className="mb-10">
            <h3 className="text-xl font-bold text-white mb-2">How to Request Deletion</h3>
            <p className="text-sm text-slate-400">Please send an email to our support team with the following details for verification:</p>
          </div>
          
          <div className="space-y-8">
            <div className="bg-[#0d0d12]/40 border border-white/5 rounded-3xl p-6 space-y-4">
               <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 text-amber-500 font-bold text-xs">1</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-200 mb-1">Required Information</h4>
                    <ul className="text-xs text-slate-500 space-y-2 list-disc ml-4">
                      <li>Full Registered Name</li>
                      <li>Registered Email Address</li>
                      <li>Associated Mobile Number</li>
                    </ul>
                  </div>
               </div>

               <div className="h-[1px] w-full bg-white/5"></div>

               <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 text-amber-500 font-bold text-xs">2</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-200 mb-1">Contact Support</h4>
                    <p className="text-xs text-slate-500 mb-2">Send your request to:</p>
                    <a href={`mailto:${supportEmail}`} className="text-amber-500 font-bold hover:underline">{supportEmail}</a>
                  </div>
               </div>
            </div>

            <div className="px-2 space-y-4">
               <div className="flex gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                  <p className="text-[11px] leading-relaxed text-slate-500 italic">
                    Account deletion requests are processed within 7 working days. Some data may be retained temporarily for legal, audit, or security purposes as per regulatory requirements.
                  </p>
               </div>
               
               <div className="pt-4">
                 <Link to="/" className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-amber-500 transition-colors flex items-center gap-2 group">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-1 transition-transform"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    Back to Portal
                 </Link>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
